/**
 * DonationPopup Component
 *
 * Modal dialog for donating to a donation-based ritual.
 * Features simplified amount input without preset chips.
 * Design matches Figma node 10705-37901.
 */

import { Loader2, XIcon } from "lucide-react"
import { useState } from "react"
import { createDonation } from "../../services/donation.api"

interface DonationPopupProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  ritualId: string
  ritualName?: string
  onDonateSuccess?: () => void
  onJoinWithoutDonation?: () => void
  onSkip?: () => void
}

export function DonationPopup({
  open,
  onOpenChange,
  ritualId,
  ritualName: _ritualName,
  onDonateSuccess: _onDonateSuccess,
  onJoinWithoutDonation,
  onSkip,
}: DonationPopupProps) {
  const [amount, setAmount] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAmountChange = (value: string) => {
    const cleanValue = value.replace(/[^0-9.]/g, "")
    const parts = cleanValue.split(".")
    if (parts.length > 2) return
    if (parts[1] && parts[1].length > 2) return
    setAmount(cleanValue)
    setError(null)
  }

  const getNumericAmount = (): number => {
    const num = parseFloat(amount)
    return Number.isNaN(num) ? 0 : num
  }

  const handleDonate = async () => {
    const numericAmount = getNumericAmount()

    if (numericAmount < 1) {
      setError("Minimum donation amount is $1.00")
      return
    }

    if (numericAmount > 10000) {
      setError("Maximum donation amount is $10,000")
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const currentUrl = window.location.href
      const successUrl = `${currentUrl}?donated=true`
      const cancelUrl = currentUrl

      const result = await createDonation(ritualId, numericAmount, successUrl, cancelUrl, true)

      if (result.data.checkoutUrl) {
        window.location.href = result.data.checkoutUrl
      }
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
        "Failed to process donation. Please try again."
      setError(errorMessage)
      setIsLoading(false)
    }
  }

  const handleSkip = () => {
    onOpenChange(false)
    onJoinWithoutDonation?.()
    onSkip?.()
  }

  const handleClose = () => {
    setAmount("")
    setError(null)
    onOpenChange(false)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center mb-0">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-[rgb(0,0,0)] backdrop-blur-[50px]"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className="relative z-10 w-[540px] rounded-[30px] border bg-[#252525] px-5 py-6"
        style={{ border: "1px solid rgba(242, 240, 235, 0.15)" }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-[80px]"
          style={{
            background: "rgba(242, 240, 235, 0.05)",
            border: "1px solid rgba(242, 240, 235, 0.15)",
          }}
          aria-label="Close"
        >
          <XIcon className="!h-3.5 !w-3.5" style={{ color: "#f2f0eb" }} />
        </button>

        {/* Content */}
        <div className="mt-5">
          {/* Title */}
          <h2 className="text-[18px] font-semibold leading-[21.94px]" style={{ color: "#f2f0eb" }}>
            Support this ritual
          </h2>

          {/* Description */}
          <p className="mt-3.5 text-[14px] leading-[22px]" style={{ color: "#ffffff" }}>
            Your contribution helps support the ritualist and allows more experiences like this to
            continue.
          </p>

          {/* Amount Input */}
          <div className="mt-6">
            <label
              className="text-[12px] uppercase tracking-wide"
              style={{ color: "rgba(242, 240, 235, 0.5)" }}
            >
              Your donation amount
            </label>
            <div className="mt-1 flex items-center">
              <span className="text-[14px] font-semibold" style={{ color: "#f2f0eb" }}>
                $
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={e => handleAmountChange(e.target.value)}
                placeholder="0"
                className="ml-1 flex-1 bg-transparent text-[14px] font-semibold outline-none"
                style={{ color: "#f2f0eb" }}
              />
            </div>
            <div className="mt-1 h-px w-full" style={{ background: "rgba(242, 240, 235, 0.2)" }} />
          </div>

          {/* Error Message */}
          {error && (
            <p className="mt-3 text-center text-[14px]" style={{ color: "#ef4444" }}>
              {error}
            </p>
          )}

          {/* Buttons */}
          <div className="mt-6 flex flex-col gap-2.5">
            {/* Donate Button */}
            <button
              onClick={handleDonate}
              disabled={isLoading || getNumericAmount() < 1}
              className="flex h-[50px] w-full items-center justify-center rounded-[100px] text-[14px] font-semibold uppercase tracking-wide transition-opacity cursor-pointer disabled:cursor-not-allowed"
              style={{
                background: "#f2f0eb",
                color: "#020202",
                opacity: isLoading || getNumericAmount() < 1 ? 0.5 : 1,
              }}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>Donate</>
              )}
            </button>

            {/* Skip Button */}
            <button
              onClick={handleSkip}
              className="flex h-[50px] w-full items-center justify-center rounded-[100px] border text-[14px] font-semibold uppercase tracking-wide transition-colors  cursor-pointer disabled:cursor-not-allowed"
              style={{
                borderColor: "#f2f0eb",
                color: "#f2f0eb",
                background: "transparent",
              }}
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
